import { DatePicker, TimePicker } from "antd/es";
import { Guid } from "guid-typescript";
import _ from "lodash";
import moment from "moment";
import { PickerSharedProps } from "rc-picker/es/Picker";
import { PureComponent, ReactNode } from "react";
import ReactDOM from "react-dom";
import isEqual from "react-fast-compare";
import withCommonEvents from "../../../shared/hoc/with-common-events";
import DynamicStyleManager from "../../../shared/utilty/dynamic-style-manager";
import { ReactSystemFunctions } from "../../../shared/utilty/react-system-functions";
import { CommonProps } from "../common-props";
import { DashboardState } from "../kuika-cl-model-runtimes";

declare let window: any;

enum Displays {
  Date = 1,
  Time = 2,
  DateTime = 3,
  Month = 4
}
interface DateProps extends PickerSharedProps<any> {
  value?: string | moment.Moment | Date;
  defaultValue?: string | moment.Moment | Date;
  format?: string;
  displayFormat?: string;
  use12Hours?: boolean;
  hourStep?: number;
  minuteStep?: number;
  secondStep?: number;
  onChange?: (value: string | null) => void;
  returnDisplayValue?: boolean;
  style?: any;
  placeholder?: string;
  disableBeforeDate?: any;
  disableAfterDate?: any;
  disableBeforeTime?: any;
  disableAfterTime?: any;
  className?: string;
  disabledDays?: (
    | "Monday"
    | "Tuesday"
    | "Wednesday"
    | "Thursday"
    | "Friday"
    | "Saturday"
    | "Sunday"
    | "Weekends"
    | "Weekdays"
  )[];
  displayType?: "datetime" | "date" | "time" | "month";
  isTableSearch?: boolean;
}

interface DateState {
  uniqueKey: Guid;
  isOpen: boolean;
  disableBeforeDate?: any;
  disableAfterDate?: any;
  disableBeforeTime?: any;
  disableAfterTime?: any;
}

class KDatePicker extends PureComponent<DateProps & CommonProps, DateState> {
  private uniqueKey = Guid.create();

  private style = new DynamicStyleManager(`kdatepicker_${this.uniqueKey}`);

  constructor(props: DateProps) {
    super(props);
    this.state = {
      uniqueKey: Guid.create(),
      isOpen: false,
      disableBeforeDate: undefined,
      disableAfterDate: undefined,
      disableBeforeTime: undefined,
      disableAfterTime: undefined
    };
  }

  componentDidMount = () => {
    this.setDynamicStyle();
    if (this.props && this.props.style && this.props.style?.fontSize) {
      const node = ReactDOM.findDOMNode(this) as Element;
      const inputs = node.getElementsByTagName("input");
      if (inputs && inputs.length) {
        for (let index = 0; index < inputs.length; index++) {
          inputs[index].style.fontSize = this.props.style?.fontSize;
        }
      }
    }
  };

  componentDidUpdate(prevProps: DateProps) {
    if (!isEqual(prevProps, this.props)) {
      this.setDynamicStyle();
    }
  }

  componentWillUnmount = () => {
    this.style.removeStyle();
  };

  getCalculatedDisplay = (): Displays => {
    if (this.props.isTableSearch) {
      const displayType = this.props.displayType;
      if (displayType === "datetime") return Displays.DateTime;
      if (displayType === "date") return Displays.Date;
      if (displayType === "time") return Displays.Time;
      if (displayType === "month") return Displays.Month;

      const fmt = this.props.format || "";
      if (!fmt) return Displays.Date;
      const detectedDisplay = this.detectDisplayFromFormat(fmt);
      if (detectedDisplay) return detectedDisplay;

      const hasHour = fmt.includes("HH") || fmt.includes("H") || fmt.includes("hh") || fmt.includes("h");
      const hasMinute = fmt.includes("mm");
      const hasSecond = fmt.includes("ss");
      const hasIsoT = fmt.includes("T");
      const hasAnyTime = hasHour || hasMinute || hasSecond || hasIsoT;
      const hasDateTokens = fmt.includes("Y") || fmt.includes("D") || fmt.includes("M");

      if (this.props.isTableSearch && typeof this.props.value === "string") {
        const strVal = this.props.value as string;
        const hasTimeInValue = strVal.includes(":") || strVal.includes("T");
        if (hasTimeInValue) return Displays.DateTime;
      }

      if (hasAnyTime && hasDateTokens) return Displays.DateTime;
      if (hasAnyTime && !hasDateTokens) return Displays.Time;
      if (fmt === "YYYY-MM") return Displays.Month;
      return Displays.Date;
    }
    if (!this.props.format || this.props.format == "") {
      if (this.props.displayType === "datetime") {
        return Displays.DateTime;
      }
      if (this.props.displayType === "date") {
        return Displays.Date;
      }
      if (this.props.displayType === "time") {
        return Displays.Time;
      }
      if (this.props.displayType === "month") {
        return Displays.Month;
      }
      return Displays.Date;
    }
    const detectedDisplay = this.detectDisplayFromFormat(this.props.format);
    if (detectedDisplay) return detectedDisplay;
    let calculatedDisplay = Displays.Date;
    switch (this.props.format) {
      case "DD/MM/YYYY":
      case "DD.MM.YYYY":
        calculatedDisplay = Displays.Date;
        break;
      case "MM/DD/YYYY":
      case "MM.DD.YYYY":
        calculatedDisplay = Displays.Date;
        break;
      case "YYYY/MM/DD":
      case "YYYY.MM.DD":
        calculatedDisplay = Displays.Date;
        break;
      case "YYYY/DD/MM":
      case "YYYY.DD.MM":
        calculatedDisplay = Displays.Date;
        break;
      case "YYYY-MM-DD":
      case "DD-MM-YYYY":
        calculatedDisplay = Displays.Date;
        break;
      case "MM/DD":
      case "MM.DD":
        calculatedDisplay = Displays.Date;
        break;
      case "D M Y":
        calculatedDisplay = Displays.Date;
        break;
      case "YYYY-MM-DD HH:mm":
        calculatedDisplay = Displays.DateTime;
        break;
      case "DD-MM-YYYY HH:mm":
        calculatedDisplay = Displays.DateTime;
        break;
      case "DD-MM-YYYY HH:mm:ss":
        calculatedDisplay = Displays.DateTime;
        break;
      case "mm:ss":
        calculatedDisplay = Displays.Time;
        break;
      case "hh:mm:ss":
        calculatedDisplay = Displays.Time;
        break;
      case "HH:mm":
        calculatedDisplay = Displays.Time;
        break;
      case "YYYY-MM":
        calculatedDisplay = Displays.Month;
        break;
      default:
        calculatedDisplay = Displays.Date;
        break;
    }
    return calculatedDisplay;
  };

  private detectDisplayFromFormat = (format: string): Displays | null => {
    if (!format) return null;
    const trimmedFormat = format.trim();
    if (!trimmedFormat) return null;
    const cleanedFormat = trimmedFormat.replace(/\[[^\]]*]/g, "");
    const hasHourToken = /H|h|k|K/.test(cleanedFormat);
    const hasMinuteToken = /m/.test(cleanedFormat);
    const hasSecondToken = /s|S/.test(cleanedFormat);
    const hasMeridiemToken = /a|A/.test(cleanedFormat);
    const hasIsoToken = cleanedFormat.includes("T");
    const hasAnyTimeToken = hasHourToken || hasMinuteToken || hasSecondToken || hasMeridiemToken || hasIsoToken;
    if (!hasAnyTimeToken) return null;
    const hasYearToken = /Y|y/.test(cleanedFormat);
    const hasDayToken = /D|d/.test(cleanedFormat);
    const hasMonthToken = /M/.test(cleanedFormat);
    const hasDateToken = hasYearToken || hasDayToken || (hasMonthToken && (hasYearToken || hasDayToken));
    if (hasDateToken) return Displays.DateTime;
    return Displays.Time;
  };

  setDynamicStyle = () => {
    this.style.setDynamicCss(this.getDynamicCss());
  };

  setValue = (value: string | moment.Moment | Date) => {
    if (moment.isMoment(value)) {
      this.handleChange(value);
      return;
    }

    if (value instanceof Date) {
      this.handleChange(moment(value));
    }

    if (typeof value === "string") {
      this.handleChange(moment(value));
    }

    if (value === null || value === undefined) {
      this.handleChange(moment());
    }
  };

  getDynamicCss = (): string => {
    const className: string = this.getClassName();
    if (!className || className.length === 0) {
      return "";
    }

    let result = "";
    if (this.props.style?.color || this.props.style?.fontFamily || this.props.style?.fontWeight) {
      result += `
          .${className.trim()} .ant-picker-input>input {
            `;
      if (this.props.style?.color) result += `color: ${this.props.style?.color} !important;`;
      if (this.props.style?.fontFamily) result += `font-family: ${this.props.style?.fontFamily} !important;`;
      if (this.props.style?.fontWeight) result += `font-weight: ${this.props.style?.fontWeight} !important;`;
      result += `;
      }`;
    }

    if (this.props.style?.fontSize || this.props.style?.fontFamily || this.props.style?.fontWeight) {
      result += `
          .${className.trim()} .ant-picker-input>input::-webkit-input-placeholder {
            `;
      if (this.props.style?.fontSize) result += `font-size: ${this.props.style?.fontSize} !important;`;
      if (this.props.style?.fontFamily) result += `font-family: ${this.props.style?.fontFamily} !important;`;
      if (this.props.style?.fontWeight) result += `font-weight: ${this.props.style?.fontWeight} !important;`;
      result += `;
      }`;
    }

    if (this.props.style?.backgroundColor?.length > 0 || this.props.style?.color?.length > 0) {
      result += `
          .${className.trim()} .ant-picker-clear {
            `;
      if (this.props.style?.backgroundColor?.length > 0)
        result += `background: ${this.props.style?.backgroundColor} !important;`;
      if (this.props.style?.color?.length > 0) result += `color: ${this.props.style?.color} !important;`;
      result += `;
      }`;
    }
    return result;
  };

  getClassName = () => {
    let result = "";
    if (this.props.className) {
      result = this.props.className;
    }
    if (!this.state.uniqueKey) {
      return result;
    }
    result = `${result} kdatepicker_${this.state.uniqueKey.toString().substring(0, 8)}`;
    return result;
  };

  disabledDate = (current) => {
    const cur = _.cloneDeep(current);
    const { disabledDays } = this.props;

    if (cur && disabledDays) {
      const day = cur.day();

      if (
        (disabledDays.includes("Sunday") && day === 0) ||
        (disabledDays.includes("Monday") && day === 1) ||
        (disabledDays.includes("Tuesday") && day === 2) ||
        (disabledDays.includes("Wednesday") && day === 3) ||
        (disabledDays.includes("Thursday") && day === 4) ||
        (disabledDays.includes("Friday") && day === 5) ||
        (disabledDays.includes("Saturday") && day === 6)
      ) {
        return true;
      }

      if (disabledDays.includes("Weekends") && (day === 0 || day === 6)) {
        return true;
      }

      if (disabledDays.includes("Weekdays") && day >= 1 && day <= 5) {
        return true;
      }
    }

    if (this.props.disableBeforeDate || this.state.disableBeforeDate) {
      const value =
        this.state.disableBeforeDate !== undefined
          ? moment(this.state.disableBeforeDate)
          : moment(this.props.disableBeforeDate);
      if (value) {
        if (this.getCalculatedDisplay() != Displays.Date) {
          if (cur && cur < moment(value)) return true;
        } else {
          if (cur.startOf("day") <= value.startOf("day").add(-1, "day")) return true;
        }
      }
    }

    if (this.props.disableAfterDate || this.state.disableAfterDate) {
      const value =
        this.state.disableAfterDate !== undefined
          ? moment(this.state.disableAfterDate)
          : moment(this.props.disableAfterDate);
      if (value) {
        if (this.getCalculatedDisplay() != Displays.Date) {
          if (cur && cur > moment(value)) return true;
        } else {
          if (cur.startOf("day") >= value.startOf("day").add(1, "day")) return true;
        }
      }
    }

    return undefined;
  };

  range = (start: number, end: number) => {
    const result: number[] = [];
    for (let i = start; i < end; i++) {
      result.push(i);
    }
    return result;
  };

  disabledTime = () => {
    const display = this.getDisplayText();

    if (display !== "Date & Time") return undefined;
    const beforeTime =
      this.props.disableBeforeTime || this.state.disableBeforeTime
        ? moment(this.props.disableBeforeTime || this.state.disableBeforeTime)
        : null;
    const afterTime =
      this.props.disableAfterTime || this.state.disableAfterTime
        ? moment(this.props.disableAfterTime || this.state.disableAfterTime)
        : null;

    if (!beforeTime && !afterTime) {
      return undefined;
    }

    const disabledBeforeHours = beforeTime ? beforeTime.hours() : 0;
    const disabledBeforeMinutes = beforeTime ? beforeTime.minutes() : 0;
    const disabledBeforeSeconds = beforeTime ? beforeTime.seconds() : 0;

    const disabledAfterHours = afterTime ? afterTime.hours() : 23;
    const disabledAfterMinutes = afterTime ? afterTime.minutes() : 59;
    const disabledAfterSeconds = afterTime ? afterTime.seconds() : 59;

    return {
      disabledHours: () => this.range(0, 24).filter((h) => h < disabledBeforeHours || h > disabledAfterHours),
      disabledMinutes: (selectedHour) => {
        if (selectedHour === disabledBeforeHours) {
          return this.range(0, 60).filter((m) => m < disabledBeforeMinutes);
        }
        if (selectedHour === disabledAfterHours) {
          return this.range(0, 60).filter((m) => m > disabledAfterMinutes);
        }
        return [];
      },
      disabledSeconds: (selectedHour, selectedMinute) => {
        if (selectedHour === disabledBeforeHours && selectedMinute === disabledBeforeMinutes) {
          return this.range(0, 60).filter((s) => s < disabledBeforeSeconds);
        }
        if (selectedHour === disabledAfterHours && selectedMinute === disabledAfterMinutes) {
          return this.range(0, 60).filter((s) => s > disabledAfterSeconds);
        }
        return [];
      }
    };

    return undefined;
  };

  setDisableBeforeDate = (value: any) => {
    this.setState({
      disableBeforeDate: value ?? undefined
    });
  };

  setDisableAfterTime = (value: any) => {
    this.setState({
      disableAfterTime: value ?? undefined
    });
  };

  setDisableBeforeTime = (value: any) => {
    this.setState({
      disableBeforeTime: value ?? undefined
    });
  };

  convertToMoment = (value: string | moment.Moment | Date) => {
    if (!value) {
      return null;
    }

    const format = this.props.isTableSearch ? this.props.format : this.getFormat();
    const isDatetime = this.getCalculatedDisplay() === Displays.DateTime;
    const isTime = this.getCalculatedDisplay() === Displays.Time;

    const useFormat = this.props.format === "hh:mm:ss" && this.props.use12Hours === false ? "HH:mm:ss" : format;

    if (moment.isMoment(value)) {
      return value.isValid() ? value.clone() : null;
    }

    if (value instanceof Date) {
      const m = moment(value);
      return m.isValid() ? m : null;
    }

    if (typeof value !== "string") {
      return null;
    }

    const strVal = value;

    const hasTimezone = /(?:Z|[+-]\d{2}:?\d{2})$/.test(strVal);

    const jsDateStringPattern = /^[A-Za-z]{3}\s[A-Za-z]{3}\s\d{1,2}\s\d{4}\s\d{2}:\d{2}:\d{2}\sGMT[+-]\d{4}$/;
    if (jsDateStringPattern.test(strVal)) {
      const parsedGmt = moment(strVal, "ddd MMM D YYYY HH:mm:ss [GMT]ZZ", true);
      if (parsedGmt.isValid()) {
        return parsedGmt;
      }
    }

    if (strVal.match(/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(\.\d+)?(?:Z|[+-]\d{2}:?\d{2})?$/)) {
      return moment(strVal);
    }

    if (isTime) {
      if (/^\d{2}:\d{2}$/.test(strVal)) {
        const today = moment().format("YYYY-MM-DD");
        return moment(`${today}T${strVal}:00`, "YYYY-MM-DDTHH:mm:ss");
      }

      if (/^\d{2}:\d{2}:\d{2}$/.test(strVal)) {
        const today = moment().format("YYYY-MM-DD");
        return moment(`${today}T${strVal}`, "YYYY-MM-DDTHH:mm:ss");
      }
    }

    const formatsToTry = [
      "YYYY-MM-DDTHH:mm:ss",
      "YYYY-MM-DD HH:mm:ss",
      "YYYY-MM-DD HH:mm",
      "YYYY-MM-DD",
      "DD-MM-YYYY HH:mm:ss",
      "DD-MM-YYYY HH:mm",
      "DD-MM-YYYY",
      "DD.MM.YYYY HH:mm:ss",
      "D.MM.YYYY HH:mm:ss",
      "DD.M.YYYY HH:mm:ss",
      "D.M.YYYY HH:mm:ss",
      "MM/DD/YYYY HH:mm:ss",
      "DD/MM/YYYY HH:mm:ss",
      "M/DD/YYYY HH:mm:ss",
      "DD/M/YYYY HH:mm:ss",
      "DD.MM.YYYY HH:mm",
      "D.MM.YYYY HH:mm",
      "MM/DD/YYYY HH:mm",
      "DD/MM/YYYY HH:mm",
      "DD.MM.YYYY",
      "D.MM.YYYY",
      "MM/DD/YYYY",
      "DD/MM/YYYY"
    ];

    if (isDatetime && !hasTimezone && !this.props.isTableSearch) {
      const isoDate = moment.utc(strVal).format();
      const parsedDate = moment(isoDate);
      if (parsedDate.isValid()) {
        return parsedDate;
      }
    }

    if (!this.props.isTableSearch) {
      for (const fmt of formatsToTry) {
        const parsedDate = moment(strVal, fmt, true);
        if (parsedDate.isValid()) {
          if (isDatetime && !hasTimezone) {
            return moment.utc(parsedDate.format(format));
          }
          return moment(parsedDate.format(format));
        }
      }
    }

    const result2 = isDatetime && !hasTimezone ? moment.utc(strVal, useFormat) : moment(strVal, useFormat);
    if (result2.isValid()) return result2;

    const asJsDate = new Date(strVal);
    if (!isNaN(asJsDate.getTime())) {
      const m = moment(asJsDate);
      if (m.isValid()) return m;
    }

    return null;
  };

  convertToString = (value: moment.Moment) => {
    if (value && moment.isMoment(value) && value.isValid()) {
      const format = this.props.isTableSearch ? this.props.format : this.getFormat();
      const isTime = this.getCalculatedDisplay() === Displays.Time;

      return value.format(format);
    }
    return null;
  };

  getFormat = () => {
    if (this.getCalculatedDisplay() === Displays.Date) {
      return "YYYY-MM-DD";
    }
    if (this.getCalculatedDisplay() === Displays.Month) {
      return "YYYY-MM";
    }

    return "YYYY-MM-DDTHH:mm:ssZ";
  };

  getDisplayFormat = () => {
    let format: string = this.props.format || "";

    if (format === "hh:mm:ss" && this.props.use12Hours === false) {
      return "HH:mm:ss";
    }

    if (!this.props.format || this.props.format === "") {
      const mlValue = localStorage.getItem("ml") || "";
      const userLocale = mlValue ? mlValue.split("_")[0] : "";
      const displayType = this.props.displayType;

      // Check for app-configured formats first
      const appDateFormat = ReactSystemFunctions.getAppDateFormat();
      const appTimeFormat = ReactSystemFunctions.getAppTimeFormat();

      if (userLocale) {
        if (displayType === "datetime") {
          if (appDateFormat && appTimeFormat) {
            const momentDateFormat = appDateFormat.replace(/dd/g, "DD").replace(/mm/g, "MM").replace(/yyyy/g, "YYYY");
            const momentTimeFormat = appTimeFormat
              .replace(/hh/g, "HH")
              .replace(/mm/g, "mm")
              .replace(/ss/g, "ss")
              .replace(/a/g, "A");
            format = `${momentDateFormat} ${momentTimeFormat}`;
          } else {
            format = `${moment.localeData(userLocale).longDateFormat("L")} ${moment
              .localeData(userLocale)
              .longDateFormat("LT")}`;
          }
        } else if (displayType === "date") {
          if (appDateFormat) {
            format = appDateFormat.replace(/dd/g, "DD").replace(/mm/g, "MM").replace(/yyyy/g, "YYYY");
          } else {
            format = moment.localeData(userLocale).longDateFormat("L");
          }
        } else if (displayType === "time") {
          if (appTimeFormat) {
            format = appTimeFormat.replace(/hh/g, "HH").replace(/mm/g, "mm").replace(/ss/g, "ss").replace(/a/g, "A");
          } else if (this.getCalculatedDisplay() === Displays.Time) {
            if (this.props.format === "mm:ss") {
              format = "mm:ss";
            } else {
              format = this.props.use12Hours ? "hh:mm:ss a" : "HH:mm:ss";
            }
          } else {
            format = this.props.use12Hours ? "hh:mm:ss a" : "HH:mm:ss";
          }
        } else if (displayType === "month") {
          format = moment.localeData(userLocale).longDateFormat("L");
        } else {
          format = "YYYY-MM-DDTHH:mm:ss";
        }
      }
    }

    if (this.props.use12Hours) {
      format = format.replaceAll("HH", "hh");

      if (!format.includes("a") && !format.includes("A")) {
        format += " A";
      }
    } else {
      format = format.replaceAll("hh", "HH");
    }

    return format;
  };

  isDateDisabled = (value: moment.Moment): boolean => {
    if (!value || !value.isValid()) return true;

    const { disabledDays } = this.props;
    if (value && disabledDays) {
      const day = value.day();

      if (
        (disabledDays.includes("Sunday") && day === 0) ||
        (disabledDays.includes("Monday") && day === 1) ||
        (disabledDays.includes("Tuesday") && day === 2) ||
        (disabledDays.includes("Wednesday") && day === 3) ||
        (disabledDays.includes("Thursday") && day === 4) ||
        (disabledDays.includes("Friday") && day === 5) ||
        (disabledDays.includes("Saturday") && day === 6)
      ) {
        return true;
      }

      if (disabledDays.includes("Weekends") && (day === 0 || day === 6)) {
        return true;
      }

      if (disabledDays.includes("Weekdays") && day >= 1 && day <= 5) {
        return true;
      }
    }

    if (this.props.disableBeforeDate || this.state.disableBeforeDate) {
      const disableValue =
        this.state.disableBeforeDate !== undefined
          ? moment(this.state.disableBeforeDate)
          : moment(this.props.disableBeforeDate);
      if (disableValue) {
        if (this.getCalculatedDisplay() != Displays.Date) {
          return value < disableValue;
        }
        return value.startOf("day") <= disableValue.startOf("day").add(-1, "day");
      }
    }
    return false;
  };

  handleChange = (value: any) => {
    if (value === null || value === undefined || value === "") {
      if (this.props.onChange) {
        this.props.onChange(null);
      }
      return;
    }

    try {
      const isDatetime = this.getCalculatedDisplay() === Displays.DateTime;
      const isTime = this.getCalculatedDisplay() === Displays.Time;
      let momentValue;

      if (moment.isMoment(value)) {
        if (isTime) {
          momentValue = value.clone();
        } else if (isDatetime) {
          momentValue = moment(value).local();
        } else {
          momentValue = value;
        }
        if (this.props.isTableSearch) {
          momentValue = value.clone();
        }
      } else {
        momentValue = this.convertToMoment(value);
      }

      if (!momentValue || !momentValue.isValid()) {
        if (this.props.onChange) {
          this.props.onChange(null);
        }
        return;
      }

      if (this.isDateDisabled(momentValue)) {
        return;
      }

      if (isDatetime) {
        momentValue = this.findFirstAvailableTime(momentValue);
      }

      const adjustedMomentValue = this.validateTimeSteps(momentValue);
      const dateValue = this.convertToString(adjustedMomentValue);

      if (this.props.onChange) {
        if (this.props.returnDisplayValue) {
          const displayValue = adjustedMomentValue.format(this.getDisplayFormat());
          this.props.onChange(displayValue);
        } else {
          this.props.onChange(dateValue);
        }
      }
    } catch (error) {
      if (this.props.onChange) {
        this.props.onChange(null);
      }
    }
  };

  getGenericValue = () => {
    const val = this.props.value as unknown;
    if (val === undefined || val === null || val === "") {
      return {};
    }
    const m = this.convertToMoment(val as any);
    if (m) {
      return { value: m };
    }
    return {};
  };

  getGenericDefaultValue = () => {
    let result: any = {};
    const def = this.props.defaultValue as unknown;
    if (def !== undefined && def !== null) {
      if (typeof def === "string") {
        if (def.trim() === "") {
          return { defaultValue: null };
        }
        result = { defaultValue: this.convertToMoment(def.trim()) };
      } else if (moment.isMoment(def) || def instanceof Date) {
        result = {
          defaultValue: this.convertToMoment(def as moment.Moment | Date)
        };
      }
    }
    return result;
  };

  getProps = () => {
    const props: any = _.clone(this.props);
    let style: any = _.clone(this.props.style);
    if (props.defaultValue) {
      delete props.defaultValue;
    }
    if (props.disabledDate) {
      delete props.disabledDate;
    }
    if (props.value) {
      delete props.value;
    }
    if (_.isString(props.value) && props.value.length === 0) {
      delete props.value;
    }
    if (!props.placeholder || props.placeholder.length === 0) {
      props.placeholder = "";
    }
    if (!style) {
      style = {
        width: "100%"
      };
    } else if (!style.width) {
      style.width = "100%";
    }
    if (style.display == "block") {
      style.display = "flex";
    } else if (style.display == "inline") {
      style.display = "inline-flex";
    }
    props.style = style;
    if (this.getCalculatedDisplay() === Displays.DateTime) {
      props.showHour = true;
      props.showMinute = true;
      props.showSecond = true;
    }

    if (this.props.hourStep !== undefined && (this.props.hourStep <= 0 || this.props.hourStep > 24)) {
      delete props.hourStep;
    }

    if (this.props.minuteStep !== undefined && (this.props.minuteStep <= 0 || this.props.minuteStep > 60)) {
      delete props.minuteStep;
    }

    if (this.props.secondStep !== undefined && (this.props.secondStep <= 0 || this.props.secondStep > 60)) {
      delete props.secondStep;
    }

    if (props.format === "hh:mm:ss" && props.use12Hours === false) {
      props.format = "HH:mm:ss";
    }

    return props;
  };

  getDisplayText = () => {
    const display = this.getCalculatedDisplay();
    let displayText = "Date";
    switch (display) {
      case Displays.Date:
        displayText = "Date";
        break;
      case Displays.DateTime:
        displayText = "Date & Time";
        break;
      case Displays.Time:
        displayText = "Time";
        break;
      case Displays.Month:
        displayText = "Month";
        break;
      default:
        displayText = "Date";
        break;
    }
    return displayText;
  };

  getDefaultTimeValue = (): moment.Moment => {
    const defaultTime = moment("00:00:00", "HH:mm:ss");

    if (this.props.disableBeforeTime) {
      const beforeTime = moment(this.props.disableBeforeTime);
      if (beforeTime.isValid()) {
        const beforeHour = beforeTime.hour();
        const beforeMinute = beforeTime.minute();
        const beforeSecond = beforeTime.second();

        if (beforeHour > 0 || beforeMinute > 0 || beforeSecond > 0) {
          return beforeTime.clone();
        }
      }
    }

    return defaultTime;
  };

  findFirstAvailableTime = (momentValue: moment.Moment): moment.Moment => {
    if (
      (!this.props.disableBeforeTime && !this.props.disableAfterTime) ||
      this.getCalculatedDisplay() !== Displays.DateTime
    ) {
      return momentValue;
    }

    const testValue = momentValue.clone();
    const currentHour = testValue.hour();
    const currentMinute = testValue.minute();
    const currentSecond = testValue.second();

    if (this.props.disableBeforeTime) {
      const beforeTime = moment(this.props.disableBeforeTime);
      if (beforeTime.isValid()) {
        const beforeHour = beforeTime.hour();
        const beforeMinute = beforeTime.minute();
        const beforeSecond = beforeTime.second();

        if (
          currentHour < beforeHour ||
          (currentHour === beforeHour && currentMinute < beforeMinute) ||
          (currentHour === beforeHour && currentMinute === beforeMinute && currentSecond < beforeSecond)
        ) {
          if (this.props.disableAfterTime) {
            const afterTime = moment(this.props.disableAfterTime);
            if (afterTime.isValid()) {
              const afterHour = afterTime.hour();
              const afterMinute = afterTime.minute();
              const afterSecond = afterTime.second();

              if (
                beforeHour > afterHour ||
                (beforeHour === afterHour && beforeMinute > afterMinute) ||
                (beforeHour === afterHour && beforeMinute === afterMinute && beforeSecond > afterSecond)
              ) {
                return testValue.hour(afterHour).minute(afterMinute).second(afterSecond).add(1, "second");
              }
            }
          }

          return testValue.hour(beforeHour).minute(beforeMinute).second(beforeSecond);
        }
      }
    }

    if (this.props.disableAfterTime) {
      const afterTime = moment(this.props.disableAfterTime);
      if (afterTime.isValid()) {
        const afterHour = afterTime.hour();
        const afterMinute = afterTime.minute();
        const afterSecond = afterTime.second();

        if (
          currentHour > afterHour ||
          (currentHour === afterHour && currentMinute > afterMinute) ||
          (currentHour === afterHour && currentMinute === afterMinute && currentSecond > afterSecond)
        ) {
          return testValue.hour(afterHour).minute(afterMinute).second(afterSecond);
        }
      }
    }

    return momentValue;
  };

  validateTimeSteps = (momentValue: moment.Moment): moment.Moment => {
    if (
      !momentValue ||
      !momentValue.isValid() ||
      !(this.props.hourStep || this.props.minuteStep || this.props.secondStep)
    ) {
      return momentValue;
    }

    const display = this.getCalculatedDisplay();
    if (display !== Displays.DateTime && display !== Displays.Time) {
      return momentValue;
    }

    const isTime = display === Displays.Time;

    let adjustedValue = momentValue.clone();

    if (!isTime) {
      const originalOffset = momentValue.utcOffset();
      adjustedValue = adjustedValue.utcOffset(originalOffset);
    }

    if (this.props.hourStep && this.props.hourStep > 0 && this.props.hourStep <= 24) {
      const currentHour = adjustedValue.hour();
      const adjustedHour = Math.round(currentHour / this.props.hourStep) * this.props.hourStep;
      adjustedValue = adjustedValue.hour(Math.min(adjustedHour, 23));
    }

    if (this.props.minuteStep && this.props.minuteStep > 0 && this.props.minuteStep <= 60) {
      const currentMinute = adjustedValue.minute();
      const adjustedMinute = Math.round(currentMinute / this.props.minuteStep) * this.props.minuteStep;
      adjustedValue = adjustedValue.minute(Math.min(adjustedMinute, 59));
    }

    if (this.props.secondStep && this.props.secondStep > 0 && this.props.secondStep <= 60) {
      const currentSecond = adjustedValue.second();
      const adjustedSecond = Math.round(currentSecond / this.props.secondStep) * this.props.secondStep;
      adjustedValue = adjustedValue.second(Math.min(adjustedSecond, 59));
    }

    return adjustedValue;
  };

  handleKeydown = (e: any) => {
    const charCode = e.which ? e.which : e.keyCode;
    if (charCode === 9 && this.state.isOpen) {
      this.setState({ isOpen: false });
      e.preventDefault();
      e.stopPropagation();
      const val = moment(e?.target?.value, this.props.format ?? "");
      if (val.isValid() && !this.isDateDisabled(val)) {
        const adjustedVal = this.validateTimeSteps(val);
        this.handleChange(moment(adjustedVal));
      }
      return false;
    }
    if (charCode === 9 && !this.state.isOpen) {
      if (this.props.onKeyDown) {
        this.props.onKeyDown(e, e.preventDefault());
      }
      return false;
    }
    const { format } = this.props;
    if (
      format !== "YYYY-MM-DD HH:mm:ss" &&
      format !== "DD-MM-YYYY HH:mm:ss" &&
      format !== "YYYY-MM-DD HH:mm" &&
      format !== "DD-MM-YYYY HH:mm" &&
      e.target.value.length === 10 &&
      charCode !== 8 &&
      charCode !== 46 &&
      charCode !== 186
    ) {
      e.preventDefault();
      e.stopPropagation();
      return false;
    }
    if (
      charCode > 31 &&
      (charCode > 57 || charCode < 48) &&
      (charCode > 105 || charCode < 96) &&
      charCode !== 8 &&
      charCode !== 46 &&
      charCode !== 186 &&
      charCode !== 190 &&
      charCode !== 110
    ) {
      e.preventDefault();
      e.stopPropagation();
      return false;
    }
    if (format === "YYYY/MM/DD" || format === "YYYY/DD/MM") {
      this.addSlashToDateInput(e, 4, 7, charCode);
    } else if (format === "YYYY-MM-DD HH:mm:ss" || format === "YYYY-MM-DD HH:mm") {
      this.addHyphenToDateInput(e, 4, 7, charCode);
    } else if (format === "DD/MM/YYYY" || format === "MM/DD/YYYY") {
      this.addSlashToDateInput(e, 2, 5, charCode);
    } else if (format === "DD-MM-YYYY HH:mm:ss" || format === "DD-MM-YYYY HH:mm") {
      this.addHyphenToDateInput(e, 2, 5, charCode);
    } else if (format === "DD.MM.YYYY" || format === "MM.DD.YYYY") {
      this.addDotToDateInput(e, 2, 5, charCode);
    } else if (format === "YYYY.MM.DD" || format === "YYYY.DD.MM") {
      this.addDotToDateInput(e, 4, 7, charCode);
    } else if (format === "YYYY-MM-DD") {
      if (e.target.value.length === 10) {
        e.preventDefault();
        e.stopPropagation();
        return false;
      }
      this.addHyphenToDateInput(e, 4, 7, charCode);
    } else if (format === "DD-MM-YYYY") {
      if (e.target.value.length === 10) {
        e.preventDefault();
        e.stopPropagation();
        return false;
      }
      this.addHyphenToDateInput(e, 2, 5, charCode);
    } else if (format === "MM/DD") {
      if (e.target.value.length === 5) {
        e.preventDefault();
        e.stopPropagation();
        return false;
      }
      this.addSlashToDateInput(e, 2, undefined, charCode);
    } else if (format === "MM.DD") {
      if (e.target.value.length === 5) {
        e.preventDefault();
        e.stopPropagation();
        return false;
      }
      this.addDotToDateInput(e, 2, undefined, charCode);
    } else if (format === "YYYY/MM") {
      if (e.target.value.length === 7) {
        e.preventDefault();
        e.stopPropagation();
        return false;
      }
      this.addSlashToDateInput(e, 4, undefined, charCode);
    } else if (format === "YYYY.MM") {
      if (e.target.value.length === 7) {
        e.preventDefault();
        e.stopPropagation();
        return false;
      }
      this.addDotToDateInput(e, 4, undefined, charCode);
    } else if (format === "D M Y") {
      e.target.value = e.target.value;
    } else if (format === undefined) {
      this.addSlashToDateInput(e, 4, 7, charCode);
    }
  };

  addSlashToDateInput = (e: any, firstLength: number, secondLength: number | undefined, charCode: number) => {
    if (e.target.value.length === firstLength && charCode !== 8 && charCode !== 46 && charCode !== 186) {
      e.target.value = `${e.target.value}/`;
    } else if (
      secondLength &&
      e.target.value.length === secondLength &&
      charCode !== 8 &&
      charCode !== 46 &&
      charCode !== 186
    ) {
      e.target.value = `${e.target.value}/`;
    } else e.target.value = e.target.value.replace(/[^0-9/]/g, "");
  };

  addDotToDateInput = (e: any, firstLength: number, secondLength: number | undefined, charCode: number) => {
    if (
      e.target.value.length === firstLength &&
      charCode !== 8 &&
      charCode !== 46 &&
      charCode !== 186 &&
      charCode !== 190 &&
      charCode !== 110
    ) {
      e.target.value = `${e.target.value}.`;
    } else if (
      secondLength &&
      e.target.value.length === secondLength &&
      charCode !== 8 &&
      charCode !== 46 &&
      charCode !== 186 &&
      charCode !== 190 &&
      charCode !== 110
    ) {
      e.target.value = `${e.target.value}.`;
    } else e.target.value = e.target.value.replace(/[^0-9.]/g, "");
  };

  addHyphenToDateInput = (e: any, firstLength: number, secondLength: number | undefined, charCode: number) => {
    if (e.target.value.length === firstLength && charCode !== 8 && charCode !== 46 && charCode !== 186) {
      e.target.value = `${e.target.value}-`;
    } else if (
      secondLength &&
      e.target.value.length === secondLength &&
      charCode !== 8 &&
      charCode !== 46 &&
      charCode !== 186
    ) {
      e.target.value = `${e.target.value}-`;
    } else e.target.value = e.target.value.replace(/[^0-9-]/g, "");
  };

  handleOpenChange = (isOpen: boolean) => {
    if (window?.kuika?.dashboardState !== DashboardState.design) {
      this.setState({ isOpen });
    }
  };

  handleBlur = (e: any) => {
    const inputValue = e?.target?.value;
    if (inputValue) {
      const val = moment(inputValue, this.getDisplayFormat());
      if (val.isValid() && !this.isDateDisabled(val)) {
        const adjustedVal = this.validateTimeSteps(val);
        this.handleChange(adjustedVal);
      }
    }
  };

  getLocale = () => {
    return ReactSystemFunctions.getLocale();
  };

  render(): ReactNode {
    const display = this.getDisplayText();
    const isDatetime = this.getCalculatedDisplay() === Displays.DateTime;
    const locale = this.getLocale();

    let timeFormat = this.props.format;
    if (timeFormat === "hh:mm:ss" && this.props.use12Hours === false) {
      timeFormat = "HH:mm:ss";
    }

    return (
      <>
        {(display === "Date" || display === "Date & Time" || display === "Month") && (
          <DatePicker
            open={this.state.isOpen}
            onOpenChange={this.handleOpenChange}
            className={this.getClassName()}
            disabledDate={this.disabledDate}
            disabledTime={this.disabledTime}
            locale={locale}
            {...this.getProps()}
            showTime={
              display === "Date & Time" || this.props.displayType === "datetime"
                ? {
                    defaultValue: this.getDefaultTimeValue(),
                    utcOffset: isDatetime ? moment().utcOffset() : undefined
                  }
                : false
            }
            picker={display === "Month" ? "month" : "date"}
            {...this.getGenericValue()}
            {...this.getGenericDefaultValue()}
            onChange={this.handleChange}
            onKeyDown={(e) => this.handleKeydown(e)}
            format={this.getDisplayFormat()}
          ></DatePicker>
        )}
        {display === "Time" && (
          <TimePicker
            open={this.state.isOpen}
            onOpenChange={this.handleOpenChange}
            className={this.getClassName()}
            locale={locale}
            {...this.getProps()}
            {...this.getGenericValue()}
            {...this.getGenericDefaultValue()}
            onChange={this.handleChange}
            onBlur={this.handleBlur}
            format={this.getDisplayFormat()}
          ></TimePicker>
        )}
      </>
    );
  }
}
const kDatePicker = withCommonEvents(KDatePicker);
export { kDatePicker as KDatePicker };
